import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import { Avatar } from 'primereact/avatar';
import { Message } from 'primereact/message';
import { OverlayPanel } from 'primereact/overlaypanel';
import { postRequest } from '../../../api/ApiController';
import AuthContext from '../../AuthContext';
import UtilContext from '../../UtilContext';
import { LocaleContext } from '../../LocaleContext';
import MyEditor from '../../Editor';
import { BotIcon } from '../../../pages/svg/svgIcons';
import { buildCrmActivityItems, sortCrmActivityItems } from '../../../utils/buildCrmActivityItems';
import { showCrmWorkflowToast } from '../../../utils/crmCommunicationPanelSave';
import { eventUserLabel, resolveCrmCommunicationEventLabel } from '../../../utils/crmCommunicationEventLabels';
import { resolveSystemI18nComment } from '../../../utils/systemI18nComment';
import { runCrmPanelMutation } from '../../../utils/crmCommunicationPanelSave';
import { extractMentionIdsFromHtml } from '../../../utils/crmCommunicationMentions';

function userDisplayName(u) {
  return u?.employee?.name || u?.username || '';
}

function systemEventContent(label, who) {
  return (
    <div className="flex align-items-center">
      <BotIcon />
      <div className="ml-2">
        <span>{label}</span>
        {who ? <span className="text-gray-600 text-sm ml-1">({who})</span> : null}
      </div>
    </div>
  );
}

/**
 * Unified activity feed (system events + comments) – parita ServiceTaskDetail.
 */
export default function CrmCommunicationActivityPanel({
  ticketId,
  ticket,
  ticketType = '',
  usersById,
  dateFmt,
  dateTimeFmt,
  onChanged,
  onTicketUpdated,
  onLocalUpdate,
  canComment = true,
}) {
  const { t } = useContext(LocaleContext);
  const { user } = useContext(AuthContext);
  const { toast, getUserColor, getUserInitials } = useContext(UtilContext);

  const [sortDescending, setSortDescending] = useState(true);
  const [commentClick, setCommentClick] = useState(false);
  const [updatableComment, setUpdatableComment] = useState(false);
  const op = useRef(null);
  const scrollRef = useRef(null);

  const activityItems = useMemo(() => {
    const built = buildCrmActivityItems(ticket?.events, ticket?.comments);
    return sortCrmActivityItems(built, sortDescending);
  }, [ticket?.comments, ticket?.events, sortDescending]);

  const lastAddedComment = useMemo(() => {
    const list = Array.isArray(ticket?.comments) ? ticket.comments : [];
    if (!list.length) return null;
    return list.reduce((latest, current) => {
      const da = DateTime.fromISO(String(latest?.created_at || '')).toMillis() || 0;
      const db = DateTime.fromISO(String(current?.created_at || '')).toMillis() || 0;
      return db >= da ? current : latest;
    });
  }, [ticket?.comments]);

  useEffect(() => {
    if (!scrollRef.current || !sortDescending) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [activityItems.length, sortDescending, ticket?.comments?.length]);

  const formikComment = useFormik({
    initialValues: { description: '' },
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        onLocalUpdate?.();
        await runCrmPanelMutation(onChanged, async () => {
          const res = await postRequest(`/api/crm_communication_tickets/${ticketId}/comments`, {
            body: values.description || '',
            mentions: extractMentionIdsFromHtml(values.description || ''),
          }, true);
          if (res?.status === 200) {
            formikComment.resetForm();
            setCommentClick(false);
            showCrmWorkflowToast(toast, t, 'ServiceTaskDetail.Toast.RecordSaved');
            if (res?.data?.id && onTicketUpdated) {
              onTicketUpdated(res.data);
            }
            return res;
          } else {
            throw new Error(String(res?.message || JSON.stringify(res?.errors || res)));
          }
        });
      } catch (e) {
        toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Toast.Error'), detail: String(e?.message || e), life: 4000 });
      }
    },
  });

  const formikUpdate = useFormik({
    initialValues: { id: '', description: '' },
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        onLocalUpdate?.();
        await runCrmPanelMutation(onChanged, async () => {
          const res = await postRequest(`/api/crm_communication_tickets/comments/update/${values.id}`, {
            body: values.description || '',
            mentions: extractMentionIdsFromHtml(values.description || ''),
          }, true);
          if (res?.status === 200) {
            formikUpdate.resetForm();
            setUpdatableComment(false);
            showCrmWorkflowToast(toast, t, 'ServiceTaskDetail.Toast.CommentUpdated');
            if (res?.data?.id && onTicketUpdated) {
              onTicketUpdated(res.data);
            }
            return res;
          } else {
            throw new Error(String(res?.message || JSON.stringify(res?.errors || res)));
          }
        });
      } catch (e) {
        toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Toast.Error'), detail: String(e?.message || e), life: 4000 });
      }
    },
  });

  const commentComposer = (position) => {
    if (!canComment) return null;
    return (
      <div className={`flex flex-row ${position === 'top' ? 'mb-3' : 'mt-3'}`}>
        <div className="basis-1/10">
          <Avatar
            className="p-overlay-badge"
            title={userDisplayName(user)}
            label={getUserInitials(userDisplayName(user))}
            shape="circle"
            style={{ backgroundColor: getUserColor(user?.username), color: '#ffffff', marginRight: '5px' }}
          />
        </div>
        {!commentClick ? (
          <div className="flex flex-col basis-9/10">
            <input
              type="text"
              onClick={() => setCommentClick(true)}
              placeholder={t('ServiceTask.AddComment')}
              className="app-activity-add-comment-input px-1 py-1 border focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
            />
          </div>
        ) : (
          <form onSubmit={formikComment.handleSubmit} className="basis-9/10">
            <div className="app-activity-comment-editor-border-wrap">
              <MyEditor editorValue={formikComment.values.description} autofocus fileAccept="*" formik={formikComment} activityMessageRadius />
            </div>
            <div className="flex flex-row gap-1">
              <button type="button" onClick={() => setCommentClick(false)} className="flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-gray-600 rounded-md hover:bg-gray-400 focus:outline-none disabled:opacity-25">{t('Common.Cancel')}</button>
              <button type="submit" className="flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600 disabled:opacity-25">{t('Common.Save')}</button>
            </div>
          </form>
        )}
      </div>
    );
  };

  const formatTs = (raw) => {
    if (!raw) return '';
    try {
      const dt = DateTime.fromISO(String(raw));
      return `${dt.toFormat(dateFmt)}${t('Common.DateTimeAt')}${dt.toFormat('HH:mm')}`;
    } catch (_) {
      return String(raw);
    }
  };

  return (
    <div>
      <div className="flex justify-between">
        <h1 className="text-xl font-semibold text-black mb-4">{t('ServiceTask.Activity')}</h1>
        <button
          type="button"
          onClick={() => setSortDescending((prev) => !prev)}
          title={sortDescending ? t('ServiceTask.SortOldestFirst') : t('ServiceTask.SortNewestFirst')}
          className="text-gray-600 hover:text-gray-800"
        >
          <i className={`pi ${sortDescending ? 'pi-sort-amount-down' : 'pi-sort-amount-up'}`} />
        </button>
      </div>
      <div ref={scrollRef} className="h-fit overflow-y-auto">
        {sortDescending ? commentComposer('top') : null}
        {activityItems.map((item) => {
          if (item.kind === 'event') {
            const row = item.event;
            const type = String(row?.type || '');
            const who = eventUserLabel(row);
            const label = resolveCrmCommunicationEventLabel(t, type, ticketType);
            const when = formatTs(row?.created_at);
            return (
              <div key={item.id} className="flex flex-row w-full justify-between mb-3">
                <div className="basis-1/10">
                  <Avatar
                    className="p-overlay-badge"
                    title={t('ServiceTask.SystemNotification')}
                    icon="pi pi-database"
                    shape="circle"
                    style={{ backgroundColor: 'grey', color: '#ffffff', marginRight: '5px' }}
                  />
                </div>
                <div className="flex flex-col grow basis-8/10 min-w-0">
                  <div className="mb-1">
                    <span className="font-semibold">{t('ServiceTask.SystemNotification')}</span>
                    {when ? <> {when}</> : null}
                  </div>
                  <Message severity="info" content={() => systemEventContent(label, who)} className="app-activity-comment-content justify-start bg-gray-100" />
                </div>
              </div>
            );
          }

          const comment = item.comment;
          const resolvedComment = resolveSystemI18nComment(comment.body, t);
          const isWorkflowActivityComment = resolvedComment.type === 'i18n';
          const author = usersById?.get?.(Number(comment.created_by_user_id));
          const authorName = comment.created_by_name
            || userDisplayName(author)
            || (comment.created_by_user_id ? `#${comment.created_by_user_id}` : '');
          const when = formatTs(comment.updated_at || comment.created_at);
          const isLastComment = lastAddedComment && Number(comment.id) === Number(lastAddedComment.id);
          const canEdit = !isWorkflowActivityComment
            && isLastComment
            && Number(comment.created_by_user_id) === Number(user?.id)
            && canComment;

          return (
            <div key={item.id} className="flex flex-row w-full justify-between mb-3">
              <div className="basis-1/10">
                <Avatar
                  className="p-overlay-badge"
                  title={authorName}
                  label={getUserInitials(authorName)}
                  shape="circle"
                  style={{
                    backgroundColor: getUserColor(author?.username ?? authorName),
                    color: '#ffffff',
                    marginRight: '5px',
                  }}
                />
              </div>
              <div className="flex flex-col grow basis-8/10 min-w-0">
                <div className="mb-1">
                  <span className="font-semibold">{authorName}</span>
                  {when ? <> {when}</> : null}
                </div>
                {isLastComment && updatableComment ? (
                  <form onSubmit={formikUpdate.handleSubmit}>
                    <div className="app-activity-comment-editor-border-wrap">
                      <MyEditor editorValue={formikUpdate.values.description} autofocus fileAccept="*" formik={formikUpdate} activityMessageRadius />
                    </div>
                    <div className="flex flex-row gap-1">
                      <button type="button" onClick={() => setUpdatableComment(false)} className="flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-gray-600 rounded-md hover:bg-gray-400 focus:outline-none disabled:opacity-25">{t('Common.Cancel')}</button>
                      <button type="submit" className="flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none disabled:opacity-25">{t('Common.Edit')}</button>
                    </div>
                  </form>
                ) : isWorkflowActivityComment ? (
                  <Message severity="info" content={() => systemEventContent(resolvedComment.text, '')} className="app-activity-comment-content justify-start bg-gray-100" />
                ) : (
                  <div className="app-activity-comment-content min-w-0">
                    <MyEditor editorValue={comment.body} hideToolbar hideFileUpload compact formik={null} activityMessageRadius />
                  </div>
                )}
              </div>
              {canEdit && !updatableComment ? (
                <div className="flex basis-1/10">
                  <button type="button" onClick={(e) => op.current.toggle(e)}><i className="pi pi-ellipsis-v" /></button>
                  <OverlayPanel ref={op}>
                    <div className="flex flex-col">
                      <button
                        type="button"
                        className="px-4 py-3 hover:bg-gray-200 text-left"
                        onClick={() => {
                          formikUpdate.setFieldValue('id', comment.id);
                          formikUpdate.setFieldValue('description', comment.body);
                          setUpdatableComment(true);
                          op.current.hide();
                        }}
                      >
                        <i className="pi pi-pencil mr-2" /><span className="font-semibold">{t('Common.Edit')}</span>
                      </button>
                    </div>
                  </OverlayPanel>
                </div>
              ) : null}
            </div>
          );
        })}
        {!sortDescending ? commentComposer('bottom') : null}
      </div>
    </div>
  );
}
